<template>
  <div class="text-sm space-y-6">
    <vf-notification v-if="error" :dismissible="false" type="error">
      {{ $t.questionsError }}
    </vf-notification>
    <template v-else>
      <h2 class="text-center title-2" data-test-id="product-questions">
        {{ $t.qAndA }}
      </h2>
      <div class="text-center" data-test-id="product-question-add">
        <vf-button class="<md:w-full" @click="DialogAskQuestion.open({ productId })">
          {{ $t.askAQuestion }}
        </vf-button>
      </div>
    </template>
    <section v-if="pending || questions?.length || questionsQuery.q" data-test-id="product-reviews-search">
      <!-- Search, Sorting -->
      <section class="space-y-4">
        <div class="bg-grey-90 p-4 text-center lg:flex lg:items-center lg:between <md:-mx-4">
          <p class="text-base <lg:mb-4">
            {{ $t.searchForAnswersToYourQuestions }}
          </p>
          <vf-search-bar
            v-model="searchValue"
            :label="$t.searchQuestions"
            :maxlength="50"
            @clear="clear"
            @submit="search"
          />
        </div>
        <div class="flex justify-end">
          <select-sort
            v-model="sort"
            :label="$t.sort"
            :options="sortOptions"
            @update:model-value="questionsQuery.sort = $event!.toString()"
          />
        </div>
      </section>
      <!-- Questions List -->
      <section
        :aria-label="`${$t.questions} - ${currentQuestionsDisplayedText}`"
        :class="currentQuestionsCount ? 'space-y-8' : 'pt-4'"
        data-scroll-el="questions-list"
      >
        <section class="pt-6 space-y-6" data-test-id="customer-questions">
          <product-reviews-question-detail
            v-for="question in questions"
            :key="question.question_id"
            :product-id
            :question
            :search-term="questionsQuery.q"
          />
        </section>

        <div class="f-col center md:flex-row md:between" :class="{ 'gap-1': pagesCount > 1 }">
          <div class="f-col center gap-2 md:flex-row md:gap-4">
            <p :class="currentQuestionsCount ? 'c-grey-20' : 'text-base'" data-test-id="display-reviews">
              {{ currentQuestionsDisplayedText }}
            </p>
            <base-button
              v-if="currentQuestionsDisplayedCount > backToTopThreshold"
              class="underlined"
              data-test-id="questions-back-to-top"
              @click="scrollToQuestions"
            >
              {{ $t.backToTop }}
            </base-button>
          </div>
          <div class="flex">
            <base-button v-if="currentPageNumber > 1" class="underlined" @click="showPrevious">
              {{ $t.previous }}
            </base-button>
            <span v-if="currentPageNumber > 1 && currentPageNumber < pagesCount" class="mx-2">|</span>
            <base-button
              v-if="pagesCount && currentPageNumber < pagesCount"
              class="underlined"
              data-test-id="questions-next"
              @click="showNext"
            >
              {{ $t.next }}
            </base-button>
          </div>
        </div>
      </section>
    </section>

    <dialog-ask-question />
    <dialog-ask-question-success />
  </div>
</template>

<script lang="ts" setup>
import type { ProductReviewsQuestionsResponse } from '#root/api/clients/product/data-contracts'
import type { ProductQuestionsParams } from '#types/components/product/reviews'

const { questionsData } = defineProps<{
  productId: string
  pending: boolean
  error: any
  questionsData?: ProductReviewsQuestionsResponse | null
}>()

const questionsQuery = defineModel<ProductQuestionsParams>({ required: true })

const { questions: questionsConfig } = useAppConfig().pages.pdp
const { DialogAskQuestionSuccess, DialogAskQuestion } = useDialogsStore()
const { $t } = useNuxtApp()

const backToTopThreshold = 3
const pageSize = questionsConfig.pageSize
const sortOptions = [
  { value: 'Newest', label: $t.reviewSortOptionNewest },
  { value: 'Oldest', label: $t.reviewSortOptionOldest }
]

const searchValue = ref()
const sort = ref('Newest')

const questions = computed(() => questionsData?.results || [])
const currentQuestionsCount = computed(() => questionsData?.paging.total_results || 0)
const pagesCount = computed(() => questionsData?.paging.pages_total || 1)
const currentPageNumber = computed(() => questionsData?.paging.current_page_number || 1)
const start = computed(() => ((currentPageNumber.value - 1) * pageSize) + 1)
const end = computed(() => Math.min(currentQuestionsCount.value, currentPageNumber.value * pageSize))
const currentQuestionsDisplayedCount = computed(() => end.value - start.value + 1)
const currentQuestionsDisplayedText = computed(() => currentQuestionsCount.value
  ? replaceAll($t.displayingQuestions, { start: start.value, end: end.value })
  : $t.noResults)

const scrollToQuestions = () => scrollToElement('questions-list')

const showPrevious = () => {
  questionsQuery.value.pagingFrom = ((currentPageNumber.value - 1) * pageSize) - pageSize
  scrollToQuestions()
}

const showNext = () => {
  questionsQuery.value.pagingFrom = ((currentPageNumber.value + 1) * pageSize) - pageSize
  scrollToQuestions()
}

const search = () => {
  questionsQuery.value.q = searchValue.value || undefined
  questionsQuery.value.pagingFrom = 0
}

const clear = async () => {
  searchValue.value = undefined
  await nextTick
  search()
}

onBeforeUnmount(() => {
  questionsQuery.value.pagingFrom = 0
  questionsQuery.value.sort = 'Newest'
  questionsQuery.value.q = undefined
})
</script>
